import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

interface FeatureCTAProps {
  title?: string;
  subtitle?: string;
  primaryText?: string;
  primaryHref?: string;
  secondaryText?: string;
  secondaryHref?: string;
}

const FeatureCTA = ({
  title = "Prêt à optimiser vos chantiers ?",
  subtitle = "Rejoignez les professionnels du BTP qui pilotent leur rentabilité en temps réel.",
  primaryText = "Essayer gratuitement 14 jours",
  primaryHref = "/auth?mode=signup",
  secondaryText,
  secondaryHref,
}: FeatureCTAProps) => {
  return (
    <section className="relative py-24 bg-[#0A0A0F] overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#F59E0B]/10 to-transparent rounded-full" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#1A365D]/20 rounded-full blur-[100px]" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#F59E0B]/5 rounded-full blur-[80px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          {/* Icon */}
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-[12px] bg-[#131318] border border-white/[0.06] mb-8">
            <Sparkles className="h-6 w-6 text-white/50" strokeWidth={2.5} />
          </div>

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {title}
          </h2>

          {/* Subtitle */}
          <p className="text-lg text-white/50 mb-10 max-w-xl mx-auto">
            {subtitle}
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="h-14 px-8 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-lg gap-3 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
              asChild
            >
              <Link to={primaryHref}>
                {primaryText}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            {secondaryText && secondaryHref && (
              <Button
                size="lg"
                variant="ghost"
                className="h-14 px-8 border border-white/[0.08] bg-white/[0.02] text-white/80 hover:text-white hover:bg-white/[0.05] hover:border-white/[0.15] font-medium text-lg rounded-xl transition-all duration-300"
                asChild
              >
                <Link to={secondaryHref}>
                  {secondaryText}
                </Link>
              </Button>
            )}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-white/40">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Sans engagement</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>100% de visibilité sur vos marges</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Support inclus</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureCTA;
